
<template>
	<div class="search-bar mb-16">
		<div
			class="search-bar__field a4-12"
			@click="focusTagSearch"
		>
			<input
				ref="tag_search_input"
				v-model="tags_filter.search_query"
				class="search__input search__input--fw-400"
				type="text"
				placeholder="Search for a tag"
				@input="handleTagSearchInputChange"
			>
		</div>
	</div>

	<div
		ref="tags_container"
		class="tags-container"
	>
		<TransitionGroup
			name="grid"
			tag="ul"
			class="tags tags--search tags--overflow-scroll"
			:class="{
				'tags--loading': loading_tags,
				'tags--overflowing': tags_overflowing
			}"
		>
			<li
				v-for="tag in non_active_tags"
				:key="'non-active-tag-' + tag.id"
				class="tags__tag"
			>
				<button @click="handleSetActiveTag( tag )">
					{{ tag.title }}
				</button>
			</li>

			<li v-if="!loading_tags && ( !non_active_tags || non_active_tags.length === 0 )">
				<p class="tags__none-found">
					No tags found
				</p>
			</li>

			<li
				v-show="show_common_tags"
				class="tags__tag tags__tag--show-all"
			>
				<button @click="showAllTags">
					Show all <template v-if="tags_filter.total_count">( {{ tags_filter.total_count }} )</template>
				</button>
			</li>
		</TransitionGroup>
	</div>

	<TransitionGroup
		name="grid"
		tag="ul"
		class="tags mt-8"
	>
		<li
			v-for="tag, index in tags_filter.active"
			:key="'active-tag-' + tag.id"
			class="tags__tag tags__tag--selected"
		>
			{{ tag.title }}
			<button
				class="tags__remove-tag"
				@click="handleRemoveActiveTag( index )"
			/>
		</li>
	</TransitionGroup>
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useDatabaseFiltersStore } from '../../stores/database-filters';
import { useProjectFiltersStore } from '../../stores/project-filters';

const props = defineProps( {
	storeKey: {
		type: String,
		default: 'projects',
	}
} );

const filters_store = props.storeKey === 'database' ? useDatabaseFiltersStore() : useProjectFiltersStore();

const {
	getTags,
	getTotalTagsCount,
	getCommonTags,
	setActiveTag,
	removeActiveTag,
	resetPagination,
	updateQueryStringFromFilters
} = filters_store;

const {
	show_filters,
	tags: tags_filter
} = storeToRefs( filters_store );

// Tags
const loading_tags = ref ( false );
const show_common_tags = ref( true );
const tag_search_input = ref( null );
const tags_overflowing = ref( false );

const tags_container = ref( null ); // Ref to container element

const tags = computed( () => {
	return show_common_tags.value ? tags_filter.value.common_tags : tags_filter.value.tags;
} );

const non_active_tags = computed( () => {
	return tags.value.filter( tag => !tags_filter.value.active.find( active_tag => active_tag.id === tag.id ) );
} );

const showAllTags = () => {
	show_common_tags.value = false;
	fetchTags();
};

const fetchTags = async() => {
	loading_tags.value = true;
	try {
		if ( !tags_filter.value.search_query && show_common_tags.value ) {
			await getCommonTags();
			return;
		}
		await getTags();
	} catch ( error ) {
		// console.log( error );
	} finally {
		loading_tags.value = false;
	}
};

const fetchTotalTagCount = async() => {
	await getTotalTagsCount();
};

const handleSetActiveTag = ( tag ) => {
	setActiveTag( tag );
	resetPagination();
	updateQueryStringFromFilters();
};

const handleRemoveActiveTag = ( index ) => {
	removeActiveTag( index );
	resetPagination();
	updateQueryStringFromFilters();
}

// Check tags list overflow
const tags_list_resize_observer = new ResizeObserver( entries => {
	entries.forEach( entry => {
		const tags_list = entry.target.firstChild ?? null;
		if ( !tags_list ) {
			return;
		}

		tags_overflowing.value = tags_list.scrollHeight > tags_list.clientHeight || tags_list.scrollWidth > tags_list.clientWidth;
	} );
} );

const handleTagSearchInputChange = async( e ) => {
	const input = e.target.value;
	setTimeout( async() => {
		if ( tags_filter.value.search_query === input ) {
			await fetchTags();
			if ( tags_filter.value.search_query ) {
				show_common_tags.value = false;
			}
		}
	}, 500 );
};

const focusTagSearch = () => {
	tag_search_input.value.focus();
};

watch( show_filters, async( new_value, old_value ) => {
	if ( new_value !== old_value && new_value === false ) {
		show_common_tags.value = true;
		await nextTick();
		fetchTags();
	}
} );

// Mount
onMounted( async() => {
	await fetchTags();
	await fetchTotalTagCount();

	loading_tags.value = false;

	await nextTick();
	tags_list_resize_observer.observe( tags_container.value );
} );
</script>